// Pantalla de consentimiento OAuth del ERP (Iter 399): ChatGPT (u otro cliente MCP) envía al usuario aquí.
// Si no hay sesión, inicia sesión con sus credenciales del ERP; luego revisa permisos y Autoriza / Rechaza.
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import api, { describirErrorServidor } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Factory, Bot, ShieldCheck, Eye, PencilLine, Loader2, AlertTriangle, LogOut } from 'lucide-react';

const PARAMS_OAUTH = ['client_id', 'redirect_uri', 'state', 'scope', 'code_challenge', 'code_challenge_method', 'resource', 'response_type'];

function LoginInline({ onLogin }) {
  const [cred, setCred] = useState({ email: '', password: '' });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try { await onLogin(cred.email, cred.password); } catch (error) { setErr(describirErrorServidor(error)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="oauth-login-form">
      <p className="text-sm text-slate-300">Inicia sesión con tu cuenta del ERP para continuar.</p>
      {err && <p role="alert" data-testid="oauth-login-error" className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">{err}</p>}
      <div className="space-y-1.5">
        <Label htmlFor="oauth-email" className="text-slate-200">Correo electrónico</Label>
        <Input id="oauth-email" type="email" required value={cred.email} onChange={(e) => setCred({ ...cred, email: e.target.value })}
          className="h-11 rounded-xl border-slate-700 bg-slate-900 text-slate-100" data-testid="oauth-login-email" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="oauth-password" className="text-slate-200">Contraseña</Label>
        <Input id="oauth-password" type="password" required value={cred.password} onChange={(e) => setCred({ ...cred, password: e.target.value })}
          className="h-11 rounded-xl border-slate-700 bg-slate-900 text-slate-100" data-testid="oauth-login-password" />
      </div>
      <Button type="submit" disabled={busy} className="h-11 w-full rounded-xl bg-orange-500 font-semibold text-white hover:bg-orange-400" data-testid="oauth-login-submit">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Iniciar sesión
      </Button>
    </form>
  );
}

function ListaPermisos({ scopes }) {
  const lectura = scopes.filter((s) => !s.escritura);
  const escritura = scopes.filter((s) => s.escritura);
  const Grupo = ({ titulo, items, icon: Icon, tone }) => items.length > 0 && (
    <div>
      <p className={`mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${tone}`}><Icon className="h-3.5 w-3.5" /> {titulo}</p>
      <ul className="space-y-1">
        {items.map((s) => <li key={s.scope} className="text-sm text-slate-200" data-testid={`oauth-scope-${s.scope.replace(':', '-')}`}>• {s.label}</li>)}
      </ul>
    </div>
  );
  return (
    <div className="space-y-4 rounded-xl border border-slate-700/80 bg-slate-900/60 p-4" data-testid="oauth-scopes">
      <Grupo titulo="Podrá consultar" items={lectura} icon={Eye} tone="text-sky-300" />
      <Grupo titulo="Podrá modificar (con tus permisos del ERP)" items={escritura} icon={PencilLine} tone="text-amber-300" />
    </div>
  );
}

export default function OAuthAutorizar() {
  const [params] = useSearchParams();
  const q = useMemo(() => Object.fromEntries(PARAMS_OAUTH.map((k) => [k, params.get(k) || ''])), [params]);
  const errorLocal = params.get('error_local');
  const { user, loading: authLoading, login, logout } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(errorLocal || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (errorLocal) return;
    if (!q.client_id || !q.redirect_uri) { setError('Solicitud incompleta: faltan client_id o redirect_uri. Vuelve a iniciar la conexión desde ChatGPT.'); return; }
    api.get('/oauth/authorize/info', { params: { client_id: q.client_id, redirect_uri: q.redirect_uri, scope: q.scope } })
      .then((r) => setInfo(r.data))
      .catch((e) => setError(e.response?.data?.detail || describirErrorServidor(e)));
  }, [q, errorLocal]);

  const decidir = async (accion) => {
    setBusy(true);
    try {
      const r = await api.post(`/oauth/authorize/${accion}`, { ...q, code_challenge_method: q.code_challenge_method || 'S256', response_type: q.response_type || 'code' });
      window.location.assign(r.data.redirect_to);
    } catch (e) {
      setError(e.response?.data?.detail || describirErrorServidor(e));
      setBusy(false);
    }
  };

  const cargando = authLoading || (!info && !error);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10 text-slate-100" data-testid="oauth-autorizar-page">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 bg-white/10"><Factory className="h-6 w-6" /></div>
          <div>
            <p className="font-heading text-xl font-bold tracking-tight">CHIRINO STEEL</p>
            <p className="text-xs text-slate-400">Autorización de acceso al ERP</p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6 shadow-2xl backdrop-blur">
          {error ? (
            <div className="space-y-3" data-testid="oauth-error">
              <div className="flex items-start gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-3 text-sm text-red-200">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div><p className="font-semibold">No se puede continuar</p><p>{error}</p></div>
              </div>
              <p className="text-xs text-slate-400">Si el problema persiste, un administrador puede revisar el cliente OAuth en Configuración → API para IAs → Conectar ChatGPT.</p>
            </div>
          ) : cargando ? (
            <div className="flex items-center justify-center py-10" data-testid="oauth-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-emerald-500/15 p-2"><Bot className="h-6 w-6 text-emerald-300" /></div>
                <div>
                  <h1 className="text-lg font-semibold leading-tight" data-testid="oauth-cliente-nombre">{info.cliente.nombre}</h1>
                  <p className="text-sm text-slate-400">quiere conectarse a tu cuenta del ERP · retorno a <span className="font-mono text-slate-300">{info.redirect_host}</span></p>
                </div>
              </div>

              {!user ? (
                <LoginInline onLogin={login} />
              ) : (
                <>
                  <div className="flex items-center justify-between rounded-xl border border-slate-700/80 bg-slate-900/60 px-3 py-2.5" data-testid="oauth-usuario">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{user.nombre || user.name || user.email}</p>
                      <p className="truncate text-xs text-slate-400">{user.email} · <Badge variant="outline" className="border-slate-600 text-[10px] text-slate-300">{user.rol}</Badge></p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-xs text-slate-300 hover:text-white" onClick={logout} data-testid="oauth-cambiar-cuenta">
                      <LogOut className="mr-1 h-3.5 w-3.5" /> Cambiar
                    </Button>
                  </div>
                  <ListaPermisos scopes={info.scopes} />
                  <p className="flex items-start gap-2 text-xs text-slate-400">
                    <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                    Cada acción se ejecutará con tu identidad, rol y permisos del ERP y quedará auditada. Puedes revocar esta conexión cuando quieras (Configuración → API para IAs).
                  </p>
                  <div className="flex gap-3 pt-1">
                    <Button variant="outline" disabled={busy} onClick={() => decidir('deny')} className="h-11 flex-1 rounded-xl border-slate-600 bg-transparent text-slate-200 hover:bg-slate-800" data-testid="oauth-rechazar">
                      Rechazar
                    </Button>
                    <Button disabled={busy} onClick={() => decidir('approve')} className="h-11 flex-1 rounded-xl bg-orange-500 font-semibold text-white hover:bg-orange-400" data-testid="oauth-autorizar">
                      {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Autorizar
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <p className="mt-6 text-center text-xs text-slate-500">© 2026 CHIRINO STEEL · OAuth 2.1 · PKCE</p>
      </div>
    </div>
  );
}
